import type { OnboardingResponse } from './onboarding'

export type ProfileFactProvenance =
  | 'user_self_report'
  | 'user_observation'
  | 'user_interpretation'
  | 'user_report_of_partner_statement'
  | 'partner_direct'
  | 'system_derived_visible'

export type ProfileFactState = 'active' | 'disputed' | 'revoked' | 'expired'
export type ProfileFactSubject = 'user' | 'partner' | 'relationship' | 'family' | 'context'
export type ProfileFactSensitivity = 'ordinary' | 'personal' | 'sensitive' | 'restricted'
export type HypothesisState = 'tentative' | 'user_confirmed_hypothesis' | 'rejected' | 'invalidated' | 'expired'

export interface ProfileFact {
  factId: string
  ownerUid: string
  subject: ProfileFactSubject
  key: string
  value: string | number | boolean | string[]
  provenance: ProfileFactProvenance
  sourceResponseIds: string[]
  confidence: 'direct' | 'high' | 'medium' | 'low' | 'unknown'
  sensitivity: ProfileFactSensitivity
  purposes: Array<'core_service' | 'ai_personalisation' | 'notifications' | 'partner_sharing'>
  state: ProfileFactState
  revision: number
  currentVersionId: string
  effectiveAt: string
  lastConfirmedAt?: string
  expiresAt?: string
  schemaVersion: 'pmm-profile-fact-1'
}

export interface ProfileFactVersion extends ProfileFact {
  versionId: string
  supersedesVersionId?: string
  correctionReason?: string
  mutationId: string
}

export interface SpineHypothesis {
  hypothesisId: string
  statement: string
  alternativeExplanation: string
  confidence: 'low' | 'medium'
  state: HypothesisState
  sourceFactIds: string[]
  createdAt: string
  expiresAt: string
  userEditedStatement?: string
  invalidationReason?: 'source_fact_changed' | 'source_fact_revoked' | 'consent_revoked'
}

export interface SpineMissingness {
  key: string
  prompt: string
  reason: 'not_answered' | 'partly_known' | 'never_explored' | 'felt_but_cannot_name' | 'inherited_or_performed' | 'temporarily_inaccessible' | 'privacy' | 'not_applicable' | 'revoked' | 'needs_partner_voice'
}

export interface RelationshipSpineVersion {
  spineVersionId: string
  ownerUid: string
  versionNumber: number
  parentSpineVersionId?: string
  sourceSnapshotId: string
  sourceSnapshotHash: string
  directFactIds: string[]
  strengthFactIds: string[]
  priorityFactIds: string[]
  constraintFactIds: string[]
  partnerReportFactIds: string[]
  hypotheses: SpineHypothesis[]
  missingness: SpineMissingness[]
  developmentalRoute: 'beginning_to_pause' | 'questioning_inherited' | 'reconnecting_and_naming' | 'practising_alignment'
  invalidatedFactIds: string[]
  userReviewState: 'needs_review' | 'reviewed'
  createdAt: string
  policyVersion: 'pmm-policy-1'
  schemaVersion: 'pmm-relationship-spine-1'
}

export interface RelationshipIntelligenceBundle {
  facts: ProfileFact[]
  factHistory: ProfileFactVersion[]
  spine: RelationshipSpineVersion
}

export interface ProfileFactCorrectionRequest {
  factId: string
  expectedRevision: number
  action: 'correct' | 'dispute' | 'revoke'
  replacementValue?: ProfileFact['value']
  reason: string
  clientMutationId: string
}

export interface SpineHypothesisReviewRequest {
  spineVersionId: string
  hypothesisId: string
  decision: 'confirm_for_now' | 'reject' | 'edit'
  editedStatement?: string
  clientMutationId: string
}

export const provenanceMeta: Record<ProfileFactProvenance, { label: string; description: string }> = {
  user_self_report: { label: 'You said', description: 'Your own current self-report.' },
  user_observation: { label: 'You observed', description: 'An event or pattern you report noticing; not verified partner truth.' },
  user_interpretation: { label: 'Your interpretation', description: 'Meaning you currently make of events; it remains correctable.' },
  user_report_of_partner_statement: { label: 'You report she said', description: 'Your report of a partner statement; not partner-direct data.' },
  partner_direct: { label: 'Shared by your partner', description: 'Partner-owned and currently consented data.' },
  system_derived_visible: { label: 'App hypothesis', description: 'A visible, tentative and expiring synthesis; never a diagnosis.' },
}

const operationalFieldIds = new Set(['orientation.honestUncertaintyConfirmed', 'agreement.selfReportConfirmed', 'agreement.partnerSovereigntyConfirmed', 'agreement.aiPreviewUnderstood', 'partner.aiUsePermission', 'review.sourceLabelsConfirmed', 'review.initialGenerationApproved'])
const strengthKeys = ['relationship.strengths', 'self.strengths', 'fatherhood.strengths', 'capacity.regulationTools']
const priorityKeys = ['relationship.changeGoals', 'self.manToBecome', 'routine.next30Days', 'fatherhood.values', 'single.readinessGoal']
const constraintKeys = ['relationship.currentStrain', 'self.currentStressors', 'capacity.current', 'capacity.sleepHours', 'routine.dailyMinutes', 'load.domestic', 'load.mental']
const missingnessFields: Array<[string, string]> = [
  ['self.coreValues', 'Which values should guide your behaviour when the relationship is under strain?'],
  ['self.manToBecome', 'Who are you practising becoming in this season?'],
  ['routine.next30Days', 'What deserves priority over the next 30 days?'],
  ['capacity.regulationTools', 'What reliably helps you return to steadiness?'],
]

export function mapOnboardingProvenance(provenance: OnboardingResponse['provenance']): ProfileFactProvenance {
  if (provenance === 'reported_by_user_about_partner') return 'user_report_of_partner_statement'
  if (provenance === 'user_observation_about_partner') return 'user_observation'
  return 'user_self_report'
}

export function factSubject(key: string): ProfileFactSubject {
  if (key.startsWith('partner.')) return 'partner'
  if (key.startsWith('relationship.') || key.startsWith('communication.') || key.startsWith('love.') || key.startsWith('load.') || key.startsWith('intimacy.')) return 'relationship'
  if (key.startsWith('children.') || key.startsWith('fatherhood.')) return 'family'
  if (key.startsWith('routine.') || key.startsWith('capacity.')) return 'context'
  return 'user'
}

export function profileFactFromOnboarding(ownerUid: string, response: OnboardingResponse, now = new Date().toISOString()): ProfileFact | null {
  if (response.sensitivity === 'high' || response.provenance === 'safety_self_report' || operationalFieldIds.has(response.fieldId)) return null
  if (response.value === '' || response.value === null || response.value === undefined) return null
  const factId = `onboarding:${response.fieldId}`
  return {
    factId,
    ownerUid,
    subject: factSubject(response.fieldId),
    key: response.fieldId,
    value: response.value as ProfileFact['value'],
    provenance: mapOnboardingProvenance(response.provenance),
    sourceResponseIds: [response.fieldId],
    confidence: response.provenance.includes('partner') ? 'medium' : response.answerState && response.answerState !== 'answered' ? 'low' : 'direct',
    sensitivity: response.sensitivity === 'low' ? 'ordinary' : 'sensitive',
    purposes: response.shareWithAi ? ['core_service', 'ai_personalisation'] : ['core_service'],
    state: 'active',
    revision: 1,
    currentVersionId: `${factId}:v1`,
    effectiveAt: now,
    lastConfirmedAt: now,
    schemaVersion: 'pmm-profile-fact-1',
  }
}

export function buildStarterSpine(ownerUid: string, snapshotId: string, facts: ProfileFact[], now = new Date().toISOString(), reflectionStates: Array<{ fieldId: string; answerState: SpineMissingness['reason'] | 'answered' | 'prefer_not_to_answer' }> = [], developmentalRoute: RelationshipSpineVersion['developmentalRoute'] = 'beginning_to_pause'): RelationshipSpineVersion {
  const active = facts.filter((fact) => fact.state === 'active')
  const byKey = new Map(active.map((fact) => [fact.key, fact]))
  const reflectionByKey = new Map(reflectionStates.map((state) => [state.fieldId, state.answerState]))
  const conflict = byKey.get('communication.conflictPattern')
  const expires = new Date(new Date(now).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
  const hypotheses: SpineHypothesis[] = conflict ? [{
    hypothesisId: 'starter:pause-before-repair',
    statement: 'Your own description suggests a pause-and-return practice may be worth testing before difficult conversations.',
    alternativeExplanation: 'Timing, workload, sleep, pain, health or another context factor may explain more of the friction than communication skill.',
    confidence: 'low',
    state: 'tentative',
    sourceFactIds: [conflict.factId],
    createdAt: now,
    expiresAt: expires,
  }] : []
  const missingness = missingnessFields
    .filter(([key]) => !byKey.has(key))
    .map(([key, prompt]) => {
      const state = reflectionByKey.get(key)
      const reason = !state || state === 'answered' ? 'not_answered' : state === 'prefer_not_to_answer' ? 'privacy' : state
      return { key, prompt, reason: reason as SpineMissingness['reason'] }
    })
  return {
    spineVersionId: `starter-${snapshotId}`,
    ownerUid,
    versionNumber: 1,
    sourceSnapshotId: snapshotId,
    sourceSnapshotHash: snapshotId,
    directFactIds: active.map((fact) => fact.factId),
    strengthFactIds: strengthKeys.flatMap((key) => byKey.get(key)?.factId ?? []),
    priorityFactIds: priorityKeys.flatMap((key) => byKey.get(key)?.factId ?? []),
    constraintFactIds: constraintKeys.flatMap((key) => byKey.get(key)?.factId ?? []),
    partnerReportFactIds: active.filter((fact) => fact.subject === 'partner').map((fact) => fact.factId),
    hypotheses,
    missingness,
    developmentalRoute,
    invalidatedFactIds: [],
    userReviewState: 'needs_review',
    createdAt: now,
    policyVersion: 'pmm-policy-1',
    schemaVersion: 'pmm-relationship-spine-1',
  }
}

export function isHypothesisExpired(hypothesis: SpineHypothesis, now = Date.now()): boolean {
  return ['tentative', 'user_confirmed_hypothesis'].includes(hypothesis.state) && new Date(hypothesis.expiresAt).getTime() <= now
}

export function displayFactValue(value: ProfileFact['value']): string {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

export function parseEditedFactValue(input: string, original: ProfileFact['value']): ProfileFact['value'] {
  if (typeof original === 'number') {
    const value = Number(input)
    if (!Number.isFinite(value)) throw new Error('Enter a valid number.')
    return value
  }
  if (typeof original === 'boolean') {
    if (!['true', 'false'].includes(input)) throw new Error('Choose true or false.')
    return input === 'true'
  }
  if (Array.isArray(original)) return input.split(',').map((item) => item.trim()).filter(Boolean)
  return input.trim()
}
